/**
 * Policy templates (Phase 3). The library sits in Guidance for everyone; each firm's workspace has its own
 * policies: generate one from a short questionnaire or with the AI policy writer (PolicyWriter.tsx), download
 * it as Word on the Baker Tilly letterhead, and record when the firm adopted it and who approved it. The firm
 * owns every policy it adopts.
 */
import { PRACTICE_QUESTIONS_STATUS, practiceQuestionsFor, type PracticeAnswers } from '@grc/shared/policies';
import { CONTROL_SUMMARIES } from '@grc/shared/vocabulary';
import { useState, type FormEvent } from 'react';
import { Link, NavLink, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, useApi, useApiClient, useDownload } from '../api';
import { Card, formatDate, Load, PageBack, PageHeader } from '../components';
import { showDraftLabel } from '../config';
import { useMe } from '../Layout';
import { NetworkBanner } from './FirmOverview';
import { filledPractice, PolicyWriter, PracticeFields } from './PolicyWriter';

interface Question {
  key: string;
  label: string;
  help?: string;
  suggestion?: string;
}
interface Template {
  id: string;
  title: string;
  headline: string;
  summary: string;
  controls: string[];
  basedOn: string[];
  questions: Question[];
}
interface Library {
  status: 'draft' | 'published';
  version: string;
  templates: Template[];
}
interface FirmPolicy {
  id: number;
  templateId: string;
  title: string;
  versionNo: number;
  status: 'draft' | 'adopted' | 'retired';
  adoptedOn: string | null;
  approvedBy: string | null;
  createdBy: string;
  createdAt: string;
  owner: string | null;
  approver: string | null;
  effectiveDate: string | null;
  /** Written with the policy writer: some clauses adapted to the firm. */
  tailored?: boolean;
}
interface FirmPolicies {
  firmCode: string;
  firmName: string;
  viewer: 'firm' | 'network';
  canUpdate: boolean;
  policies: FirmPolicy[];
}

const STATUS: Record<FirmPolicy['status'], { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  adopted: { label: 'Adopted', tone: 'in_place' },
  retired: { label: 'Retired', tone: 'neutral' },
};

/** Essential Eight chips name the strategy, so two E8 controls never read "E8 E8" (bug fix, 2026-10-03). */
const E8_SHORT: Record<string, string> = {
  'E8-APP-CONTROL': 'E8 App control',
  'E8-APP-HARDENING': 'E8 App hardening',
  'E8-BACKUPS': 'E8 Backups',
  'E8-MFA': 'E8 MFA',
  'E8-OFFICE-MACROS': 'E8 Office macros',
  'E8-PATCH-APPS': 'E8 Patch apps',
  'E8-PATCH-OS': 'E8 Patch OS',
};
const controlChip = (id: string) => (id.startsWith('E8-') ? (E8_SHORT[id] ?? 'Essential Eight') : `CIS ${id}`);

export function GuidanceTabs() {
  return (
    <nav className="tabs" aria-label="Guidance sections">
      <NavLink to="/guidance" end>
        Security controls
      </NavLink>
      <NavLink to="/guidance/policies">Policy templates</NavLink>
      <NavLink to="/guidance/resources">How National can help</NavLink>
    </nav>
  );
}

function ControlList({ ids }: { ids: string[] }) {
  return (
    <ul className="plain chip-list" aria-label="Controls it helps meet">
      {ids.map((id) => (
        <li key={id}>
          <Link className="chip tone-neutral" to={`/guidance/controls/${id}`} title={CONTROL_SUMMARIES[id]}>
            {controlChip(id)}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function PolicyTemplates() {
  const state = useApi<Library>('/policy-templates');
  const me = useMe();
  const download = useDownload();
  const itFirms = (me.data?.firms ?? []).filter((f) => f.canWrite);
  return (
    <>
      <PageBack to="/guidance">Back to guidance</PageBack>
      <PageHeader
        eyebrow="Guidance"
        title="Policy templates"
        lede="Baseline policies that help meet the CIS controls, in the Baker Tilly format. Download a blank template, or create one for your firm with your details filled in."
      />
      <GuidanceTabs />
      <Load state={state} what="policy templates">
        {(lib) => (
          <>
            {showDraftLabel(lib.status) && <p className="notice">Draft templates for National IT review. Wording may change before they are published.</p>}
            <div className="cards two">
              {lib.templates.map((t) => (
                <section key={t.id} className="card policy-card" aria-labelledby={`pt-${t.id}`}>
                  <h2 id={`pt-${t.id}`}>{t.title}</h2>
                  <p className="policy-headline">{t.headline}</p>
                  <p className="small">{t.summary}</p>
                  <ControlList ids={t.controls} />
                  <div className="actions">
                    <button className="pill secondary-dark" onClick={() => download(`/policy-templates/${t.id}/document`, `Baker Tilly ${t.title} template.docx`)}>
                      Download blank template
                    </button>
                    {itFirms.map((f) => (
                      <Link key={f.firmCode} className="pill" to={`/firm/${f.firmCode}/policies?template=${t.id}`}>
                        {itFirms.length > 1 ? `Create for ${f.displayName}` : 'Create for my firm'}
                      </Link>
                    ))}
                  </div>
                  <p className="muted small">Draws on: {t.basedOn.join('; ')}.</p>
                </section>
              ))}
            </div>
            <p className="muted small">
              Each template maps its clauses to the CIS Controls v8.1 safeguards and ASD Essential Eight strategies it supports. Adopting a policy is one step toward a control;
              the control must also work in practice. Templates version {lib.version}.
            </p>
          </>
        )}
      </Load>
    </>
  );
}

export function FirmPoliciesPage() {
  const { firmCode } = useParams();
  const state = useApi<FirmPolicies>(`/firms/${firmCode}/policies`);
  const lib = useApi<Library>('/policy-templates');
  const [params, setParams] = useSearchParams();
  const chosen = params.get('template');
  return (
    <Load state={state} what="policies">
      {(d) => (
        <>
          <NetworkBanner viewer={d.viewer} firmName={d.firmName} />
          <p className="page-back">
            Back to the <Link to={`/firm/${d.firmCode}`}>dashboard</Link>, or see <Link to="/guidance/policies">all policy templates</Link>
          </p>
          <PageHeader title="Policies" lede={`${d.firmName}'s policies created from the National templates. Your firm reviews, adopts and owns each one.`} />
          {d.canUpdate && lib.data && (
            <CreatePolicy
              key={chosen ?? 'none'}
              firm={d}
              templates={lib.data.templates}
              chosen={chosen}
              onChoose={(id) => setParams(id ? { template: id } : {}, { replace: true })}
              onCreated={() => {
                setParams({}, { replace: true });
                state.reload();
              }}
            />
          )}
          <PolicyList d={d} onChanged={state.reload} />
        </>
      )}
    </Load>
  );
}

function CreatePolicy({ firm, templates, chosen, onChoose, onCreated }: { firm: FirmPolicies; templates: Template[]; chosen: string | null; onChoose: (id: string | null) => void; onCreated: () => void }) {
  const call = useApiClient();
  const download = useDownload();
  const t = templates.find((x) => x.id === chosen) ?? null;
  const last = firm.policies[0];
  const defaults = Object.fromEntries(
    (t?.questions ?? []).map((q) => [q.key, q.key === 'firm' ? firm.firmName : q.key === 'owner' && last?.owner ? last.owner : q.key === 'approver' && last?.approver ? last.approver : q.suggestion ?? '']),
  );
  const [answers, setAnswers] = useState<Record<string, string>>(defaults);
  // DEC-49: the policy writer is the default way; the short form stays one click away.
  const [mode, setMode] = useState<'questions' | 'writer'>('writer');
  // Answers to the policy's own questions, kept across both ways, and handed to the writer when it starts.
  const [practice, setPractice] = useState<PracticeAnswers>({});
  const practiceQs = t ? practiceQuestionsFor(t) : [];
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!t) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await call<{ id: number; versionNo: number }>(`/firms/${firm.firmCode}/policies`, { method: 'POST', body: { templateId: t.id, answers } });
      await download(`/firms/${firm.firmCode}/policies/${r.id}/document`, `${firm.firmName} ${t.title} v${r.versionNo}.docx`);
      onCreated();
    } catch (err) {
      setMessage({ ok: false, text: err instanceof ApiError ? err.message : 'The policy was not created.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Create a policy">
      <label>
        Policy
        <select value={chosen ?? ''} onChange={(e) => onChoose(e.target.value || null)}>
          <option value="">Choose a policy…</option>
          {templates.map((x) => (
            <option key={x.id} value={x.id}>
              {x.title}
            </option>
          ))}
        </select>
      </label>
      {t && (
        <>
          <p className="small">
            <strong>{t.headline}.</strong> {t.summary}
          </p>
          <ControlList ids={t.controls} />
          <div className="mode-choice" role="group" aria-label="How to create it">
            <button type="button" aria-pressed={mode === 'writer'} onClick={() => setMode('writer')}>
              <strong>Write it with the policy writer</strong>
              <span>Answer this policy's questions or describe what your firm does. The policy writer asks what it still needs and adapts the policy to your tools.</span>
            </button>
            <button type="button" aria-pressed={mode === 'questions'} onClick={() => setMode('questions')}>
              <strong>Answer a few questions</strong>
              <span>A short form. The National wording, with your firm's details filled in.</span>
            </button>
          </div>
        </>
      )}
      {t && mode === 'writer' && (
        <PolicyWriter
          firmCode={firm.firmCode}
          firmName={firm.firmName}
          title={t.title}
          templateId={t.id}
          questions={t.questions}
          defaults={defaults}
          practice={practice}
          onPractice={setPractice}
          onCreated={onCreated}
        />
      )}
      {t && mode === 'questions' && (
        <form className="form" onSubmit={submit} aria-label={`Create the ${t.title}`}>
          <h3 className="q-head">Policy details</h3>
          {/* Help text sits under each field, so the fields in a row line up. */}
          <div className="q-grid">
            {t.questions.map((q) => (
              <label key={q.key} className="q-field">
                <span className="q-label">{q.label}</span>
                <input
                  type={q.key === 'effectiveDate' ? 'date' : 'text'}
                  required
                  maxLength={200}
                  value={answers[q.key] ?? ''}
                  onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                  aria-describedby={q.help ? `help-${q.key}` : undefined}
                />
                {q.help && (
                  <span id={`help-${q.key}`} className="muted small">
                    {q.help}
                  </span>
                )}
              </label>
            ))}
          </div>
          {practiceQs.length > 0 && (
            <fieldset className="practice-set">
              <legend>About your firm's practice</legend>
              <p className="muted small">
                Optional questions for the {t.title}. The policy writer uses your answers to adapt the policy to what {firm.firmName} does. The National wording below
                does not change with them.
              </p>
              {showDraftLabel(PRACTICE_QUESTIONS_STATUS) && <p className="notice small">These questions are a draft for National IT review.</p>}
              <PracticeFields questions={practiceQs} answers={practice} onChange={setPractice} idPrefix="pq" />
              <div className="actions">
                <button type="button" className="pill secondary-dark" disabled={busy || !Object.keys(filledPractice(practice)).length} onClick={() => setMode('writer')}>
                  Use these answers with the policy writer
                </button>
              </div>
            </fieldset>
          )}
          <div className="actions">
            <button className="pill" disabled={busy}>
              {busy ? 'Creating…' : 'Create and download'}
            </button>
          </div>
          <p className="muted small">
            Create and download uses the National wording with the policy details filled in. The draft opens in Word on the Baker Tilly letterhead. Edit it as you need, then
            record here when your firm adopts it.
          </p>
        </form>
      )}
      {message && (
        <p role="alert" className="notice">
          {message.text}
        </p>
      )}
    </Card>
  );
}

function PolicyList({ d, onChanged }: { d: FirmPolicies; onChanged: () => void }) {
  const download = useDownload();
  const [adopting, setAdopting] = useState<number | null>(null);
  if (!d.policies.length) {
    return (
      <Card title="Your firm's policies">
        <p className="muted">No policies created yet.{d.canUpdate ? ' Choose one above to start.' : ''}</p>
      </Card>
    );
  }
  return (
    <Card title="Your firm's policies">
      <div className="table-wrap" tabIndex={0} role="region" aria-label="Your firm's policies">
        <table>
          <thead>
            <tr>
              <th scope="col">Policy</th>
              <th scope="col">Version</th>
              <th scope="col">Status</th>
              <th scope="col">Effective</th>
              <th scope="col">Created</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {d.policies.map((p) => (
              <tr key={p.id}>
                <th scope="row">
                  {p.title}
                  {p.tailored && <div className="muted small">Written with the policy writer</div>}
                </th>
                <td>{p.versionNo}</td>
                <td>
                  <span className={`chip tone-${STATUS[p.status].tone}`}>{STATUS[p.status].label}</span>
                  {p.status === 'adopted' && (
                    <div className="muted small">
                      {formatDate(p.adoptedOn)}, approved by {p.approvedBy}
                    </div>
                  )}
                </td>
                <td>{p.effectiveDate ? formatDate(p.effectiveDate) : ''}</td>
                <td>
                  {formatDate(p.createdAt)} <span className="muted small">by {p.createdBy}</span>
                </td>
                <td>
                  <div className="actions">
                    <button className="link" onClick={() => download(`/firms/${d.firmCode}/policies/${p.id}/document`, `${d.firmName} ${p.title} v${p.versionNo}.docx`)}>
                      Download
                    </button>
                    {d.canUpdate && p.status !== 'adopted' && (
                      <button className="link" onClick={() => setAdopting(adopting === p.id ? null : p.id)}>
                        Record adoption
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adopting !== null && <AdoptForm d={d} policy={d.policies.find((p) => p.id === adopting)!} onDone={() => (setAdopting(null), onChanged())} />}
    </Card>
  );
}

function AdoptForm({ d, policy, onDone }: { d: FirmPolicies; policy: FirmPolicy; onDone: () => void }) {
  const call = useApiClient();
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ adoptedOn: today, approvedBy: '' });
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="form"
      aria-label={`Record adoption of ${policy.title}`}
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        try {
          await call(`/firms/${d.firmCode}/policies/${policy.id}`, { method: 'PATCH', body: { status: 'adopted', ...f } });
          onDone();
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Not saved.');
        }
      }}
    >
      <h3>Record adoption: {policy.title}, version {policy.versionNo}</h3>
      <div className="row">
        <label>
          Adopted on
          <input type="date" required max={today} value={f.adoptedOn} onChange={(e) => setF({ ...f, adoptedOn: e.target.value })} />
        </label>
        <label>
          Approved by (name and role)
          <input required maxLength={200} value={f.approvedBy} onChange={(e) => setF({ ...f, approvedBy: e.target.value })} placeholder={policy.approver ?? 'for example, Jordan Lee, Managing Partner'} />
        </label>
      </div>
      <div className="actions">
        <button className="pill">Save</button>
      </div>
      {error && (
        <p role="alert" className="notice">
          {error}
        </p>
      )}
    </form>
  );
}
